import { create } from "zustand";
import { Movie, MovieBookmarkType } from "../utils/types";
import {
  addDocument,
  getCollectionDocuments,
  removeDocument,
  updateDocumentField,
} from "../service/firebaseStore";
import { getUserId } from "../utils/functions";
import { createJSONStorage, persist } from "zustand/middleware";
import { showToast } from "../components/common/Toast";


interface FirebaseState {
  watchListMovies: MovieBookmarkType[];
  favoriteMovies: MovieBookmarkType[];

  fetchWatchListMovies: () => void;
  fetchFavoriteMovies: () => void;
  addToWatchList: (movie: Movie) => void;
  addToFavourites: (movie: Movie) => void;
  removeFromWatchList: (id: number) => void;
  removeFromFavorite: (id: number) => void;
  pinMovie: (id: number) => void;
  unpinMovie: (id: number) => void;
  updateMovieInWatchList: (movie: Movie) => Promise<void>;
  clearFirebaseStore: () => void;
  isLoading: boolean;
}

export const useFirebaseStore = create<FirebaseState>()(
  persist(
    (set, get) => ({
      watchListMovies: [],
      favoriteMovies: [],
      isLoading: false,
      updateMovieInWatchList: async (movie: Movie) => {
        const { watchListMovies, favoriteMovies } = get();
        const movieInWatchList = watchListMovies.find(
          (m) => m.movieId === movie.id,
        );
        const movieInFavorite = favoriteMovies.find(
          (m) => m.movieId === movie.id,
        );

        const hasChanged = (existing: MovieBookmarkType | undefined) => {
          if (!existing) return false;
          return (
            existing.movie.popularity !== movie.popularity ||
            existing.movie.vote_count !== movie.vote_count ||
            existing.movie.vote_average !== movie.vote_average ||
            existing.movie.backdrop_path !== movie.backdrop_path ||
            existing.movie.poster_path !== movie.poster_path
          );
        };

        if (hasChanged(movieInWatchList)) {
          const updatedItem = {
            ...movieInWatchList,
            movie: JSON.parse(JSON.stringify(movie)),
          };
          console.log({ updatedItem });
          await updateDocumentField<MovieBookmarkType>(
            "bookmarks",
            movie.id,
            updatedItem,
          );
          set((state) => ({
            ...state,
            watchListMovies: state.watchListMovies.map((m) =>
              m.movieId === movie.id ? updatedItem : m,
            ),
          }));
          return;
        }

        if (hasChanged(movieInFavorite)) {
          const updatedItem = {
            ...movieInFavorite,
            movie: JSON.parse(JSON.stringify(movie)),
          };
          await updateDocumentField<MovieBookmarkType>(
            "favorites",
            movie.id,
            updatedItem,
          );
          set((state) => ({
            ...state,
            favoriteMovies: state.favoriteMovies.map((m) =>
              m.movieId === movie.id ? updatedItem : m,
            ),
          }));
        }
      },
      clearFirebaseStore: () =>
        set((state) => ({
          ...state,
          watchListMovies: [],
          favoriteMovies: [],
        })),
      fetchWatchListMovies: async () => {
        set((state) => ({ ...state, isLoading: true }));
        const watchListMovies =
          await getCollectionDocuments<MovieBookmarkType>("bookmarks");
        if (watchListMovies) {
          // Sort by pinnedAt timestamp (latest first), fallback to timeStamp if pinnedAt is not available
          const sortedWatchListMovies =
            getSortedWatchListMovies(watchListMovies);

          set((state) => ({
            ...state,
            watchListMovies: sortedWatchListMovies,
            isLoading: false,
          }));
        } else {
          set((state) => ({ ...state, isLoading: false }));
        }
      },
      fetchFavoriteMovies: async () => {
        set((state) => ({ ...state, isLoading: true }));
        const favoriteMovies =
          await getCollectionDocuments<MovieBookmarkType>("favorites");
        if (favoriteMovies) {
          set((state) => ({
            ...state,
            favoriteMovies,
            isLoading: false,
          }));
        } else {
          set((state) => ({ ...state, isLoading: false }));
        }
      },
      addToWatchList: async (movie: Movie) => {
        try {
          const userId = getUserId();
          if (userId) {
            const payload: MovieBookmarkType = {
              movieId: movie.id,
              userId: userId,
              movie,
              timeStamp: new Date().getTime(),
            };
            const success = await addDocument<MovieBookmarkType>(payload, "bookmarks");
            if (success) {
              set((state) => ({
                ...state,
                watchListMovies: [payload, ...state.watchListMovies],
              }));
              showToast(`"${movie.title}" added to WatchList!`, "success");
            } else {
              showToast(`Failed to add "${movie.title}" to WatchList`, "error");
            }
          } else {
            showToast("Please login to add movies to your WatchList", "warning");
          }
        } catch (err) {
          console.error("Error in addToWatchList", err);
          showToast(`Error adding "${movie.title}" to WatchList`, "error");
        }
      },
      addToFavourites: async (movie: Movie) => {
        try {
          const userId = getUserId();
          if (userId) {
            const payload: MovieBookmarkType = {
              movieId: movie.id,
              userId: userId,
              movie,
              timeStamp: new Date().getTime(),
            };
            const success = await addDocument<MovieBookmarkType>(payload, "favorites");
            if (success) {
              set((state) => ({
                ...state,
                favoriteMovies: [payload, ...state.favoriteMovies],
              }));
              showToast(`"${movie.title}" added to Favorites!`, "success");
            } else {
              showToast(`Failed to add "${movie.title}" to Favorites`, "error");
            }
          } else {
            showToast("Please login to add movies to Favorites", "warning");
          }
        } catch (err) {
          console.error("Error in addToFavourites", err);
          showToast(`Error adding "${movie.title}" to Favorites`, "error");
        }
      },
      removeFromWatchList: async (id: number) => {
        try {
          const watchListMovies = get().watchListMovies;
          const movieItem = watchListMovies.find((item) => item.movieId === id);
          const movieTitle = movieItem?.movie.title || "Movie";

          const success = await removeDocument("bookmarks", id);
          if (success) {
            set((state) => ({
              ...state,
              watchListMovies: state.watchListMovies.filter(
                (i) => i.movieId !== id,
              ),
            }));
            showToast(`"${movieTitle}" removed from WatchList!`, "success");
          } else {
            showToast(`Failed to remove "${movieTitle}" from WatchList`, "error");
          }
        } catch (err) {
          console.error("Error in removeFromWatchList", err);
          showToast("Error removing movie from WatchList", "error");
        }
      },
      removeFromFavorite: async (id: number) => {
        try {
          const favoriteMovies = get().favoriteMovies;
          const movieItem = favoriteMovies.find((item) => item.movieId === id);
          const movieTitle = movieItem?.movie.title || "Movie";

          const success = await removeDocument("favorites", id);
          if (success) {
            set((state) => ({
              ...state,
              favoriteMovies: state.favoriteMovies.filter(
                (i) => i.movieId !== id,
              ),
            }));
            showToast(`"${movieTitle}" removed from Favorites!`, "success");
          } else {
            showToast(`Failed to remove "${movieTitle}" from Favorites`, "error");
          }
        } catch (err) {
          console.error("Error in removeFromFavorite", err);
          showToast("Error removing movie from Favorites", "error");
        }
      },
      pinMovie: async (id: number) => {
        try {
          const watchListMovies = get().watchListMovies;
          const movieItem = watchListMovies.find((item) => item.movieId === id);
          if (!movieItem) return;
          const movieTitle = movieItem.movie.title;
          const updatedItem = {
            ...movieItem,
            pinnedAt: new Date().getTime(),
          };
          const success = await updateDocumentField<MovieBookmarkType>(
            "bookmarks",
            id,
            updatedItem,
          );
          if (success) {
            set((state) => {
              return {
                ...state,
                watchListMovies: state.watchListMovies.map((item) => {
                  if (item.movieId === id) {
                    return updatedItem;
                  }
                  return item;
                }),
              };
            });
            showToast(`"${movieTitle}" pinned to top!`, "success");
          } else {
            showToast(`Failed to pin "${movieTitle}"`, "error");
          }
        } catch (err) {
          console.error("Error in pinMovie", err);
          showToast("Error pinning movie", "error");
        }
      },
      unpinMovie: async (id: number) => {
        try {
          const watchListMovies = get().watchListMovies;
          const movieItem = watchListMovies.find((item) => item.movieId === id);
          if (!movieItem) return;
          const movieTitle = movieItem.movie.title;
          const updatedItem = {
            ...movieItem,
            pinnedAt: 0,
            timeStamp: new Date().getTime(),
          };
          const success = await updateDocumentField<MovieBookmarkType>(
            "bookmarks",
            id,
            updatedItem,
          );
          if (success) {
            set((state) => {
              const movies = state.watchListMovies.map((item) =>
                item.movieId === id ? updatedItem : item,
              );
              const watchListMovies = getSortedWatchListMovies(movies);
              return {
                ...state,
                watchListMovies,
              };
            });
            showToast(`"${movieTitle}" unpinned!`, "success");
          } else {
            showToast(`Failed to unpin "${movieTitle}"`, "error");
          }
        } catch (err) {
          console.error("Error in unpinMovie", err);
          showToast("Error unpinning movie", "error");
        }
      },
    }),
    {
      name: "firebase-storage",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

const getSortedWatchListMovies = (movies: MovieBookmarkType[]) => {
  return movies.sort((a, b) => {
    const aPinned = a.pinnedAt ?? 0;
    const bPinned = b.pinnedAt ?? 0;
    if (aPinned !== bPinned) {
      return bPinned - aPinned; // Sort by pinnedAt descending
    }
    return (b.timeStamp ?? 0) - (a.timeStamp ?? 0); // Fallback to timeStamp descending
  });
};
